/**
 * Single Source of Truth (SSOT) for platform products / editions (Task #3148).
 *
 * A "product" is the commercial edition a tenant belongs to (`tenants.product`).
 * Backend, tenant frontend and master frontend all import from here — no
 * surface may hardcode the product list.
 *
 * IMPORTANT: `key` values must NEVER be changed once shipped — they are stored
 * verbatim in `tenants.product` (DB CHECK constraint) and in JWT claims.
 * Adding a product requires: (1) a new entry here, (2) a dbmate migration that
 * widens the CHECK constraint on `tenants.product`.
 */
import { z } from 'zod';
import type { ModuleKey } from './modules';

export interface ProductDefinition {
  /** Stable machine key, persisted in `tenants.product` and JWT claim `product`. */
  key: string;
  /** Human-readable label for UI surfaces. */
  label: string;
  /** Default module profile seeded into `tenant_modules` at tenant creation. */
  defaultModules: ReadonlyArray<ModuleKey>;
  /**
   * Host pubblici DEPLOYATI posseduti dal prodotto (superficie tenant).
   * `[0]` è l'host canonico (cert Let's Encrypt, vhost, DNS); gli altri sono
   * alias (www, api, ws, …) che il vhost di prodotto serve per lo stesso FE.
   *
   * È la fonte di verità statica dell'ownership host→prodotto: il boundary
   * tenant (`resolveProductForPublicHost`), il generatore dei vhost LB-1/FE-1
   * (`scripts/deploy/product-edge.ts`) e il gate anti-literal
   * (`check-no-hardcoded-brand-domain.js`) derivano tutti da qui. Gli origin
   * runtime (`PRODUCT_PUBLIC_ORIGINS`) devono restare coerenti con questa
   * lista: `validateProductPublicOriginsEnv` lo impone fail-closed al boot.
   *
   * Solo hostname minuscoli, senza schema/porta/path. Un host appartiene a UN
   * solo prodotto (`assertProductRegistryHostsUnique`).
   */
  publicHosts: readonly [string, ...string[]];
}

export const PRODUCT_REGISTRY = [
  {
    key: 'easyflow',
    label: 'Easyflow',
    defaultModules: ['pbx', 'crm', 'ai_tools', 'messenger', 'calendar'],
    publicHosts: [
      'easyflowai.it',
      'www.easyflowai.it',
      'api.easyflowai.it',
      'app.easyflowai.it',
      'ws.easyflowai.it',
    ],
  },
  {
    key: 'w3suite',
    label: 'W3 Suite',
    defaultModules: ['pbx', 'crm', 'ai_tools', 'messenger', 'calendar'],
    publicHosts: ['w3suite.it', 'www.w3suite.it'],
  },
] as const satisfies ReadonlyArray<ProductDefinition>;

export type TenantProduct = typeof PRODUCT_REGISTRY[number]['key'];

export const PRODUCT_KEYS = PRODUCT_REGISTRY.map((p) => p.key) as ReadonlyArray<TenantProduct>;

const PRODUCT_HOSTNAME_PATTERN = /^(?=.{1,253}$)[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/;

/**
 * Invariante del registry: ogni `publicHosts` è un hostname minuscolo valido
 * e appartiene a UN solo prodotto. Lanciata al primo import del modulo così
 * un registry incoerente non arriva mai a servire richieste (fail-closed).
 */
export function assertProductRegistryHostsUnique(
  registry: ReadonlyArray<ProductDefinition> = PRODUCT_REGISTRY
): void {
  const owners = new Map<string, string>();
  for (const product of registry) {
    for (const host of product.publicHosts) {
      if (!PRODUCT_HOSTNAME_PATTERN.test(host)) {
        throw new Error(
          `PRODUCT_REGISTRY: host "${host}" del prodotto "${product.key}" non è un hostname minuscolo valido`
        );
      }
      const owner = owners.get(host);
      if (owner !== undefined && owner !== product.key) {
        throw new Error(
          `PRODUCT_REGISTRY: host "${host}" dichiarato sia da "${owner}" che da "${product.key}"`
        );
      }
      owners.set(host, product.key);
    }
  }
}

assertProductRegistryHostsUnique();

/**
 * Mappa host pubblico deployato → prodotto proprietario, derivata dal registry.
 * Unica fonte per l'ownership statica (nessuna mappa parallela altrove).
 */
export function getProductPublicHostOwnership(): ReadonlyMap<string, TenantProduct> {
  const map = new Map<string, TenantProduct>();
  for (const product of PRODUCT_REGISTRY) {
    for (const host of product.publicHosts) map.set(host, product.key);
  }
  return map;
}

/** Host canonico (primo di `publicHosts`) del prodotto. */
export function getProductCanonicalHost(product: TenantProduct): string {
  const definition = PRODUCT_REGISTRY.find((p) => p.key === product);
  if (!definition) throw new Error(`Prodotto sconosciuto: ${product}`);
  return definition.publicHosts[0];
}

export const DEFAULT_PRODUCT: TenantProduct = 'easyflow';

/** Zod schema per validare il prodotto al boundary HTTP (derivato dal registry). */
export const TenantProductSchema = z.enum(
  PRODUCT_KEYS as [TenantProduct, ...TenantProduct[]]
);

export function getProductDefinition(key: string): ProductDefinition | undefined {
  return PRODUCT_REGISTRY.find((p) => p.key === key);
}

export function isTenantProduct(key: string): key is TenantProduct {
  return PRODUCT_KEYS.includes(key as TenantProduct);
}
